import type { Locker, LockerAssignment, LockerRoom } from '@/types';
import { formatHeldDuration, heldSecondsSince, memberDisplayName } from '@/lib/locker-utils';
import { ROOM_THEME } from './room-theme';

interface LockerGridProps {
  /** Lockers of the active room, already sorted by sort_order. */
  lockers: Locker[];
  /** Active room — free tiles carry the room's border tint. */
  room: LockerRoom;
  /** Open assignment per locker id (returned_at IS NULL). */
  openByLockerId: Map<string, LockerAssignment>;
  /** Epoch ms "now" from the page's minute ticker, so held-for stays live. */
  now: number;
  onSelectFree: (locker: Locker) => void;
  /** Omit to render occupied tiles as inert (no return-key flow, e.g. inside pickers). */
  onSelectOccupied?: (locker: Locker, assignment: LockerAssignment) => void;
  /** Free tile rendered as selected (emerald ring) — for picker hosts. */
  selectedLockerId?: string | null;
  /** Overrides the viewport-breakpoint column classes for container-constrained hosts (modals). */
  gridClassName?: string;
}

function initials(assignment: LockerAssignment): string {
  const first = assignment.member?.first_name?.[0] ?? '';
  const last = assignment.member?.last_name?.[0] ?? '';
  return `${first}${last}`.toUpperCase() || '?';
}

/**
 * The visual locker board: one tile per physical locker in door order. Free
 * tiles are white/green, occupied tiles amber with the holder's initials.
 */
export default function LockerGrid({ lockers, room, openByLockerId, now, onSelectFree, onSelectOccupied, selectedLockerId, gridClassName }: LockerGridProps) {
  return (
    <div className={gridClassName ?? 'grid grid-cols-5 sm:grid-cols-8 md:grid-cols-10 lg:grid-cols-12 gap-2'}>
      {lockers.map((locker) => {
        const assignment = openByLockerId.get(locker.id);

        if (!assignment) {
          const isSelected = locker.id === selectedLockerId;
          return (
            <button
              key={locker.id}
              type="button"
              onClick={() => onSelectFree(locker)}
              aria-pressed={isSelected}
              title={`Locker ${locker.label} — free`}
              className={`aspect-square rounded-lg border-2 transition-colors flex flex-col items-center justify-center ${
                isSelected
                  ? 'border-emerald-500 bg-emerald-50 text-emerald-900 ring-2 ring-emerald-500/40'
                  : ROOM_THEME[room].tile
              }`}
            >
              <span className="text-sm font-semibold">{locker.label}</span>
            </button>
          );
        }

        const heldFor = formatHeldDuration(heldSecondsSince(assignment.assigned_at, now));
        return (
          <button
            key={locker.id}
            type="button"
            onClick={onSelectOccupied ? () => onSelectOccupied(locker, assignment) : undefined}
            disabled={!onSelectOccupied}
            title={`Locker ${locker.label} — ${memberDisplayName(assignment.member)}, out ${heldFor}`}
            className={`aspect-square rounded-lg border-2 border-amber-300 bg-amber-100 text-amber-900 transition-colors flex flex-col items-center justify-center ${
              onSelectOccupied ? 'hover:border-amber-500 hover:bg-amber-200' : 'cursor-default opacity-90'
            }`}
          >
            <span className="text-sm font-semibold">{locker.label}</span>
            <span className="text-[10px] font-medium leading-tight">{initials(assignment)}</span>
            <span className="text-[9px] leading-tight hidden sm:block">{heldFor}</span>
          </button>
        );
      })}
    </div>
  );
}
